'use client'

import * as React from 'react'

const STUDIO_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_CF_URL ?? ''

interface FeaturedTemplateItemProps {
  name: string
  thumbnailUrl: string | undefined
  videoUrl: string | undefined
  'data-card'?: true
}

export function FeaturedTemplateItem({
  name,
  thumbnailUrl,
  videoUrl,
  ...rest
}: FeaturedTemplateItemProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)

  // Touch devices have no hover, so — like studio's StyleCard — fall back to autoplaying the video
  // once the card scrolls into view. Detected once on mount to keep the effect below simple.
  const [isTouch, setIsTouch] = React.useState(false)
  React.useEffect(() => {
    setIsTouch(window.matchMedia('(pointer: coarse)').matches)
  }, [])

  const play = React.useCallback(() => {
    const video = videoRef.current
    if (!video) {
      return
    }
    video
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => {})
  }, [])

  const stop = React.useCallback(() => {
    const video = videoRef.current
    if (!video) {
      return
    }
    setIsPlaying(false)
    video.pause()
    video.currentTime = 0
  }, [])

  React.useEffect(() => {
    const el = containerRef.current
    if (!isTouch || !videoUrl || !el) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          play()
        } else {
          stop()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isTouch, videoUrl, play, stop])

  return (
    <div ref={containerRef} className="shrink-0 w-[130px] lg:w-[160px] snap-center" {...rest}>
      <a
        href={`${STUDIO_BASE_URL}/ai-video-generator`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={name}
        onMouseEnter={isTouch ? undefined : play}
        onMouseLeave={isTouch ? undefined : stop}
        onFocus={isTouch ? undefined : play}
        onBlur={isTouch ? undefined : stop}
        className="group relative block aspect-[190/285] w-full overflow-hidden rounded-sm border border-cf-sf-gray-350/40 bg-cf-grey-200 focus-visible:ring-2 focus-visible:ring-cf-green focus-visible:outline-none"
      >
        {thumbnailUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbnailUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
          />
        ) : null}
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            loop
            playsInline
            preload="none"
            className={`pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-200 ${
              isPlaying ? 'opacity-100' : 'opacity-0'
            }`}
            onError={e => e.stopPropagation()}
          />
        ) : null}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-cf-black/70 to-transparent p-2">
          <span className="text-sm font-semibold font-poppins text-cf-white line-clamp-1">
            {name}
          </span>
        </div>
      </a>
    </div>
  )
}
